import { inject, Injectable, Signal, signal, WritableSignal, OnDestroy } from '@angular/core';
import {
  CreateWorkerRequest,
  UpdateWorkerRequest,
  WorkerByIdResponse,
  WorkerClient,
  WorkerExportAvailableDatesResponse,
  WorkerExportRequest,
} from '../../core/openapi/bff-admin-api';
import {
  BehaviorSubject,
  catchError,
  finalize,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import { HttpResponse } from '@angular/common/http';
import { ModalService, ToastService } from '@ui-lib/components';
import { TranslateService } from '@ngx-translate/core';
import { outputError } from '../../shared/utils/output-error.function';
import { LoadingState } from '../../shared/types/loading.type';
import { IWorkersSearchParams } from './models';
import { DEFAULT_TABLE_PAGE_SIZE } from '../../shared/const/pagination.constants';
import { MIME_TYPES } from '../../shared/const/mime-types';
import { downloadBlob } from '../../shared/utils/download.function';
import { resolveExportFileName } from '../../shared/utils/file-name.function';
import { toExportDayBounds, toReportDate } from '../../shared/utils/report-date.function';
import { ExportDayModalComponent } from '../../shared/components/export-day-modal/export-day-modal.component';
import { IExportDayModalData } from '../../shared/components/export-day-modal/export-day-modal.model';

const WORKER_EXPORT_DAY_TITLE = 'monitoring.export-day.workers-title';

@Injectable()
export class WorkersService implements OnDestroy {
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly modalService = inject(ModalService);

  private readonly workerClient: WorkerClient = inject(WorkerClient);

  private readonly subscriptionDestroy$: Subject<void> = new Subject<void>();

  private readonly _workersDataCount: WritableSignal<number> = signal<number>(0);
  private readonly _workersLoading: WritableSignal<LoadingState> = signal<LoadingState>(
    LoadingState.LOADING,
  );
  private readonly _workerCreateLoading: WritableSignal<LoadingState> = signal<LoadingState>(
    LoadingState.IDLE,
  );
  private readonly _workerUpdateLoading: WritableSignal<LoadingState> = signal<LoadingState>(
    LoadingState.IDLE,
  );
  private readonly _workerDeleteLoading: WritableSignal<LoadingState> = signal<LoadingState>(
    LoadingState.IDLE,
  );
  private readonly _workerByIdLoading: WritableSignal<LoadingState> = signal<LoadingState>(
    LoadingState.IDLE,
  );
  private readonly _workerExportLoading: WritableSignal<LoadingState> = signal<LoadingState>(
    LoadingState.IDLE,
  );

  private workerReqParams$: BehaviorSubject<IWorkersSearchParams> =
    new BehaviorSubject<IWorkersSearchParams>({
      skip: 0,
      take: DEFAULT_TABLE_PAGE_SIZE,
    });

  public ngOnDestroy(): void {
    this.subscriptionDestroy$.next();
    this.subscriptionDestroy$.complete();
  }

  public get workersDataCount(): Signal<number> {
    return this._workersDataCount.asReadonly();
  }

  public get workersLoadingStatus(): Signal<LoadingState> {
    return this._workersLoading.asReadonly();
  }

  public get workerCreateLoadingStatus(): Signal<LoadingState> {
    return this._workerCreateLoading.asReadonly();
  }

  public get workerUpdateLoadingStatus(): Signal<LoadingState> {
    return this._workerUpdateLoading.asReadonly();
  }

  public get workerDeleteLoadingStatus(): Signal<LoadingState> {
    return this._workerDeleteLoading.asReadonly();
  }

  public get workerByIdLoadingStatus(): Signal<LoadingState> {
    return this._workerByIdLoading.asReadonly();
  }

  public get workerExportLoadingStatus(): Signal<LoadingState> {
    return this._workerExportLoading.asReadonly();
  }

  private readonly getWorkers$ = this.workerReqParams$.pipe(
    tap(() => this._workersLoading.set(LoadingState.LOADING)),
    switchMap((params) =>
      this.workerClient.v1WorkerSearchPost(params).pipe(
        tap(({ totalCount }) => {
          this._workersDataCount.set(totalCount ?? 0);
        }),
        map(({ items }) => ({
          items: items ?? [],
        })),
        catchError((error) => {
          this.toastService.error({
            message: this.translateService.instant(
              'monitoring.workers.toast.workers-fetching-failed',
              { error: outputError(error, this.translateService.instant('errors.unknown')) },
            ),
          });

          this._workersDataCount.set(0);
          return of({ items: [], totalCount: 0 });
        }),
        finalize(() => this._workersLoading.set(LoadingState.READY)),
      ),
    ),
    takeUntil(this.subscriptionDestroy$),
  );

  public readonly workers$ = this.getWorkers$.pipe(map(({ items }) => items));

  public changeReqParams(params: Partial<IWorkersSearchParams>): void {
    this.workerReqParams$.next({
      ...this.workerReqParams$.value,
      ...params,
    });
  }

  public reloadWorkers(): void {
    this.workerReqParams$.next({
      ...this.workerReqParams$.value,
    });
  }

  public getWorkerById(workerId: string): Observable<WorkerByIdResponse | null> {
    this._workerByIdLoading.set(LoadingState.LOADING);

    return this.workerClient.v1WorkerIdGet(workerId).pipe(
      catchError((error) => {
        this.toastService.error({
          message: this.translateService.instant('monitoring.workers.toast.worker-fetch-failed', {
            error: outputError(error, this.translateService.instant('errors.unknown')),
          }),
        });

        return of(null);
      }),
      finalize(() => this._workerByIdLoading.set(LoadingState.READY)),
    );
  }

  public createWorker(request: CreateWorkerRequest): Observable<boolean> {
    if (this._workerCreateLoading() === LoadingState.LOADING) {
      return of(false);
    }

    this._workerCreateLoading.set(LoadingState.LOADING);

    return this.workerClient.v1WorkerPost(request).pipe(
      tap(() => {
        this.toastService.success({
          message: this.translateService.instant('monitoring.workers.toast.worker-create-success'),
        });
      }),
      map(() => true),
      catchError((error) => {
        this.toastService.error({
          message: this.translateService.instant('monitoring.workers.toast.worker-create-failed', {
            error: outputError(error, this.translateService.instant('errors.unknown')),
          }),
        });

        return of(false);
      }),
      finalize(() => this._workerCreateLoading.set(LoadingState.READY)),
    );
  }

  public updateWorker(request: UpdateWorkerRequest): Observable<boolean> {
    if (this._workerUpdateLoading() === LoadingState.LOADING) {
      return of(false);
    }

    this._workerUpdateLoading.set(LoadingState.LOADING);

    return this.workerClient.v1WorkerPut(request).pipe(
      tap(() => {
        this.toastService.success({
          message: this.translateService.instant('monitoring.workers.toast.worker-update-success'),
        });
      }),
      map(() => true),
      catchError((error) => {
        this.toastService.error({
          message: this.translateService.instant('monitoring.workers.toast.worker-update-failed', {
            error: outputError(error, this.translateService.instant('errors.unknown')),
          }),
        });

        return of(false);
      }),
      finalize(() => this._workerUpdateLoading.set(LoadingState.READY)),
    );
  }

  public deleteWorker(workerId: string): Observable<boolean> {
    if (this._workerDeleteLoading() === LoadingState.LOADING) {
      return of(false);
    }

    this._workerDeleteLoading.set(LoadingState.LOADING);

    return this.workerClient.v1WorkerIdDelete(workerId).pipe(
      tap(() => {
        this.toastService.success({
          message: this.translateService.instant('monitoring.workers.toast.worker-delete-success'),
        });
      }),
      map(() => true),
      catchError((error) => {
        this.toastService.error({
          message: this.translateService.instant('monitoring.workers.toast.worker-delete-failed', {
            error: outputError(error, this.translateService.instant('errors.unknown')),
          }),
        });

        return of(false);
      }),
      finalize(() => this._workerDeleteLoading.set(LoadingState.READY)),
    );
  }

  public exportWorkers(): void {
    if (this._workerExportLoading() === LoadingState.LOADING) {
      return;
    }

    this._workerExportLoading.set(LoadingState.LOADING);

    this.workerClient
      .v1WorkerExportAvailableDatesGet()
      .pipe(
        map((response: WorkerExportAvailableDatesResponse) => toExportDayBounds(response)),
        catchError(() => of(null)),
        take(1),
        takeUntil(this.subscriptionDestroy$),
      )
      .subscribe((bounds) => {
        if (bounds === null) {
          this._workerExportLoading.set(LoadingState.READY);

          this.toastService.error({
            message: this.translateService.instant('monitoring.workers.toast.worker-export-failed'),
          });

          return;
        }

        this.openExportDayModal(bounds);
      });
  }

  private openExportDayModal(bounds: Omit<IExportDayModalData, 'titleTranslateKey'>): void {
    this.modalService
      .open<IExportDayModalData, Date>({
        component: ExportDayModalComponent,
        customContainerClass: 'export-day-modal-container',
        data: {
          titleTranslateKey: WORKER_EXPORT_DAY_TITLE,
          earliestDate: bounds.earliestDate,
          latestDate: bounds.latestDate,
        },
      })
      .afterClose$.pipe(take(1), takeUntil(this.subscriptionDestroy$))
      .subscribe((day) => {
        if (!(day instanceof Date)) {
          this._workerExportLoading.set(LoadingState.READY);

          return;
        }

        this.sendWorkerExport(day);
      });
  }

  private sendWorkerExport(reportDate: Date): void {
    const params = this.workerReqParams$.value;
    const request: WorkerExportRequest = {
      keyword: params.keyword,
      coinIds: params.coinIds,
      poolIds: params.poolIds,
      statusIds: params.statusIds,
      reportDate: toReportDate(reportDate),
    };

    this._workerExportLoading.set(LoadingState.LOADING);

    this.workerClient
      .v1WorkerExportPost(request, 'response', false, {
        httpHeaderAccept: MIME_TYPES.EXCEL,
        responseType: 'blob',
      } as never)
      .pipe(
        map(({ body, headers }: HttpResponse<Blob | null>) =>
          body === null ? null : { body, headers },
        ),
        catchError(() => of(null)),
        finalize(() => this._workerExportLoading.set(LoadingState.READY)),
      )
      .subscribe((result) => {
        if (result === null) {
          this.toastService.error({
            message: this.translateService.instant('monitoring.workers.toast.worker-export-failed'),
          });

          return;
        }

        downloadBlob(result.body, resolveExportFileName(result.headers, 'workers_export', 'xlsx'));

        this.toastService.success({
          message: this.translateService.instant('monitoring.workers.toast.worker-export-success'),
        });
      });
  }
}
